import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { errorToastMessage } from '../../../components/layout/toast-root/toast';
import { useFileList } from '../../../hooks/use-file-list';
import { useGetFilePaths } from '../../../redux/api/appConfigApi';
import type { IPostsViewPost } from '../../../redux/api/posts/posts-api.types';
import { useDeletePost, usePostPutPost } from '../../../redux/api/postsApi';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { FileVisibility } from '../../../shared/constants';
import { IAppFile } from '../../../types/interfaces/app-file';
import { IPost } from '../../../types/interfaces/post';
import {
  deleteFilesFromFirebase,
  uploadFileAndCreateAppFile,
} from '../../../utils/files/firebase-storage-utils';

export function usePosts(
  newPostBinding?: { communityId: string } | { pageUserId: string },
  refreshPosts?: () => void,
) {
  const { t } = useTranslation();
  const [deletePost] = useDeletePost();
  const [postPutPost] = usePostPutPost();
  const { data: user } = useGetMyDetails();
  const { data: filePaths } = useGetFilePaths();

  const [newPost, setNewPost] = useState<IPost>({ content: '' });
  const [isSavingPost, setIsSavingPost] = useState(false);
  const [isDeletingPost, setIsDeletingPost] = useState(false);

  const {
    filesToUpload: newPostFilesToUpload,
    onDeleteFile: onDeleteNewPostFile,
    onUploadFiles: onUploadNewPostFiles,
    resetFilesState: resetNewPostFilesState,
  } = useFileList([]);

  function onEditPost(value: { content?: string; parentId?: string }) {
    setNewPost((prev) => ({ ...prev, ...value }));
  }

  function onPostDelete(postId) {
    setIsDeletingPost(true);

    return deletePost({ id: postId })
      .then((response) => {
        if ('error' in response) {
          console.log(response.error);
        } else {
          refreshPosts && refreshPosts();
        }
      })
      .finally(() => {
        setIsDeletingPost(false);
      });
  }

  async function onSavePost(repliedPost?: IPostsViewPost) {
    let postToSave;
    let filesToUpload;

    if (repliedPost) {
      postToSave = repliedPost;
      filesToUpload = [];
    } else {
      if ((!newPost.content && !newPostFilesToUpload.length) || !newPostBinding)
        return;

      postToSave = { ...newPostBinding, ...newPost };
      filesToUpload = newPostFilesToUpload;
    }

    if (!user || !filePaths || !user.uid || !user.id) {
      return;
    }

    setIsSavingPost(true);
    const uploadedStoragePaths: string[] = [];
    let hasError = false;

    try {
      const uploadedFiles: IAppFile[] = await Promise.all(
        filesToUpload.map(async (file) => {
          const uploadedFile = await uploadFileAndCreateAppFile(
            file,
            filePaths.posts,
            user.uid,
            user.id,
            FileVisibility.public,
          );

          uploadedStoragePaths.push(uploadedFile.storageLink);

          return uploadedFile;
        }),
      );

      await postPutPost({
        body: {
          post: postToSave as IPost,
          filesToUpload: uploadedFiles,
        },
        id: postToSave.id,
      }).unwrap();

      setNewPost({ content: '' });
      resetNewPostFilesState();
      refreshPosts && refreshPosts();
    } catch (error) {
      hasError = true;
      console.error(
        'Error uploading files to Firebase Storage or saving post:',
        error,
      );
      errorToastMessage(t('messages.uploadOrSaveFailed'));
    } finally {
      if (hasError && uploadedStoragePaths.length > 0) {
        await deleteFilesFromFirebase(uploadedStoragePaths);
      }
      setIsSavingPost(false);
    }
  }

  return {
    onPostDelete,
    onSavePost,
    onEditPost,
    onDeleteNewPostFile,
    onUploadNewPostFiles,
    newPost,
    newPostFilesToUpload,
    isSavingPost,
    isDeletingPost,
  };
}
